import { useState, type FormEvent, type JSX } from "react";
import type { Faction } from "@stahlfront/shared";
import { getMe, login, register } from "../api.js";
import { useStore } from "../store.js";

type Mode = "register" | "login";

const FACTION_DISPLAY: Record<Faction, string> = {
  USSR: "Soviet Union",
  WEHRMACHT: "Wehrmacht",
  US: "United States",
  UK: "United Kingdom",
};

const FACTION_ACCENT: Record<Faction, string> = {
  USSR: "var(--accent-faction-ussr)",
  WEHRMACHT: "var(--accent-faction-wehrmacht)",
  US: "var(--accent-faction-us)",
  UK: "var(--accent-faction-uk)",
};

function friendlyError(raw: string): string {
  if (raw.includes("HANDLE_TAKEN") || raw.includes(" 409")) {
    return "That handle is already in use on this server. Try Login or pick another handle.";
  }
  if (raw.includes("BAD_CREDENTIALS") || raw.includes(" 401")) {
    return "Wrong handle or password.";
  }
  if (raw.includes("NO_SUCH_CITY")) {
    return "Selected city no longer exists. Go back and pick another.";
  }
  if (raw.includes("BAD_BODY")) {
    return "Form data invalid. Handle 2-32 characters, password ≥ 8.";
  }
  return raw.replace(/^Error:\s*/, "");
}

export function AuthScreen(): JSX.Element {
  const city = useStore((s) => s.selectedCity);
  const setMe = useStore((s) => s.setMe);
  const setCurrentScreen = useStore((s) => s.setCurrentScreen);
  const [mode, setMode] = useState<Mode>("register");
  const [handle, setHandle] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!city) {
    return (
      <div className="sb-screen">
        <main className="sb-main">
          <div className="sb-auth-placeholder" role="alert">
            <h2>No city selected</h2>
            <p>Pick a city from the server browser first.</p>
            <button
              type="button"
              className="sb-retry"
              onClick={() => setCurrentScreen("server-browser")}
            >
              ← Back to city pick
            </button>
          </div>
        </main>
      </div>
    );
  }

  const faction = city.faction;
  const factionLabel = FACTION_DISPLAY[faction];
  const factionAccent = FACTION_ACCENT[faction];

  async function submit(ev: FormEvent<HTMLFormElement>): Promise<void> {
    ev.preventDefault();
    if (!city) return;
    setBusy(true);
    setErr(null);
    try {
      if (mode === "register") {
        await register({
          handle,
          password,
          faction: city.faction,
          serverId: city.serverId,
          cityId: city.id,
        });
      } else {
        await login({ handle, password });
      }
      const me = await getMe();
      if (!me) throw new Error("session-not-set");
      setMe(me);
      setCurrentScreen("game");
    } catch (e) {
      setErr(friendlyError(String(e)));
    } finally {
      setBusy(false);
    }
  }

  const submitLabel =
    mode === "register" ? (busy ? "Creating…" : "Create commander") : busy ? "Signing in…" : "Sign in";

  return (
    <div className="sb-screen">
      <header className="sb-topbar" aria-label="Auth screen">
        <div className="sb-topbar-left">
          <button
            type="button"
            className="sb-back"
            onClick={() => setCurrentScreen("server-browser")}
            aria-label="Back to city pick"
            title="Back to city pick"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                d="M15 6 L9 12 L15 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h2 className="sb-title">Join {city.displayName}</h2>
        </div>
        <div className="sb-topbar-right">
          <span className="sb-faction-pill" style={{ background: factionAccent }}>
            {factionLabel}
          </span>
        </div>
      </header>
      <main className="sb-main">
        <form className="auth-card" onSubmit={submit} aria-busy={busy} noValidate>
          <div className="auth-mode-tabs" role="tablist" aria-label="Authentication mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "register"}
              className={`auth-tab ${mode === "register" ? "auth-tab-active" : ""}`}
              onClick={() => setMode("register")}
              disabled={busy}
            >
              Register
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className={`auth-tab ${mode === "login" ? "auth-tab-active" : ""}`}
              onClick={() => setMode("login")}
              disabled={busy}
            >
              Login
            </button>
          </div>
          <p className="auth-context">
            {mode === "register" ? "Create a new commander" : "Sign in to existing roster"} on{" "}
            <strong>{city.displayName}</strong>, Сервер {city.serverId}.
          </p>
          <label className="auth-field">
            <span>Handle</span>
            <input
              type="text"
              autoComplete="username"
              minLength={2}
              maxLength={32}
              required
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              disabled={busy}
              placeholder="2–32 characters"
            />
          </label>
          <label className="auth-field">
            <span>Password</span>
            <input
              type="password"
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              minLength={8}
              maxLength={128}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              placeholder="≥ 8 characters"
            />
          </label>
          {err && (
            <div className="auth-error" role="alert">
              {err}
            </div>
          )}
          <button
            type="submit"
            className="auth-submit"
            disabled={busy || handle.length < 2 || password.length < 8}
          >
            {submitLabel}
          </button>
        </form>
      </main>
    </div>
  );
}
